import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import { SendCampaignNowModal } from '../SendCampaignNowModal';
import {
  User,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Layers,
  Bug,
  RotateCcw,
  Eye,
  Ban,
  FileText,
  Download,
  Trash2,
  Send
} from 'lucide-react';

interface Campaign {
  id: string;
  user_id: string;
  blog_id: string | null;
  subject: string;
  content: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  user_first_name: string;
  user_email: string;
  user_company_name: string;
  blog_title: string | null;
  success_rate_percent: number;
  failure_rate_percent: number;
  batch_count: number;
  alert_is_failed: boolean;
  alert_is_late_started: boolean;
  alert_has_high_failure_rate: boolean;
  alert_is_stuck_sending: boolean;
  minutes_late: number;
  minutes_sending: number;
}

interface Batch {
  batch_number: number;
  batch_id: string;
  batch_api_call_id: string;
  batch_size: number;
  sent_at: string;
  duration_ms: number;
  response_status_code: number;
  emails_succeeded: number;
  emails_failed: number;
  emails_pending: number;
  success_rate_percent: number;
}

interface ErrorLog {
  id: string;
  recipient_email: string;
  error_message: string;
  sent_at: string;
  batch_api_call_id: string;
}

interface ActivityLog {
  timestamp: string;
  event: string;
  icon: 'error' | 'success' | 'info' | 'warning';
  description: string;
}

interface CampaignDetailPanelProps {
  campaignId: string;
  onClose: () => void;
  onOpenDebugger: () => void;
  onRetry: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

export function CampaignDetailPanel({
  campaignId,
  onClose,
  onOpenDebugger,
  onRetry,
  onCancel,
  onDelete
}: CampaignDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'debug' | 'logs'>('overview');
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [showSendNowModal, setShowSendNowModal] = useState(false);

  useEffect(() => {
    loadCampaignDetails();
  }, [campaignId]);

  const loadCampaignDetails = async () => {
    try {
      setLoading(true);

      const { data: campaignData, error: campaignError } = await supabase
        .from('admin_campaign_alerts_view')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (campaignError) throw campaignError;

      const { data: campaignContent } = await supabase
        .from('email_campaigns')
        .select('content')
        .eq('id', campaignId)
        .single();

      setCampaign({
        ...campaignData,
        content: campaignContent?.content || ''
      });

      const { data: batchData } = await supabase.rpc('get_campaign_batch_statistics', {
        p_campaign_id: campaignId
      });

      setBatches(batchData || []);

      const { data: errorData } = await supabase
        .from('email_send_logs')
        .select('id, recipient_email, error_message, sent_at, batch_api_call_id')
        .eq('campaign_id', campaignId)
        .eq('status', 'failed')
        .order('sent_at', { ascending: false })
        .limit(50);

      setErrorLogs(errorData || []);

      const logs: ActivityLog[] = [];

      if (campaignData.sent_at) {
        logs.push({
          timestamp: campaignData.sent_at,
          event: campaignData.status === 'failed' ? 'Campaign Failed' : 'Campaign Completed',
          icon: campaignData.status === 'failed' ? 'error' : 'success',
          description: `Campaign ${campaignData.status}`
        });
      }

      if (batchData) {
        batchData.forEach((batch: Batch) => {
          logs.push({
            timestamp: batch.sent_at,
            event: `Batch ${batch.batch_number}`,
            icon: batch.emails_failed > 0 ? 'warning' : 'success',
            description: `Sent ${batch.emails_succeeded}/${batch.batch_size} emails`
          });
        });
      }

      if (campaignData.scheduled_at) {
        logs.push({
          timestamp: campaignData.scheduled_at,
          event: 'Campaign Scheduled',
          icon: 'info',
          description: 'Campaign scheduled for sending'
        });
      }

      logs.push({
        timestamp: campaignData.created_at,
        event: 'Campaign Created',
        icon: 'info',
        description: 'Campaign draft created'
      });

      logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setActivityLogs(logs);

    } catch (err) {
      console.error('Error loading campaign details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !campaign) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    const styles = {
      draft: 'bg-orange-100 text-orange-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[campaign.status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {campaign.status}
      </span>
    );
  };

  const renderOverviewTab = () => (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Campaign Info</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{campaign.subject}</p>
              <p className="text-xs text-gray-500 mt-1">Campaign Subject</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <User className="w-4 h-4 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {campaign.user_first_name} ({campaign.user_company_name})
              </p>
              <p className="text-xs text-gray-500 mt-1">{campaign.user_email}</p>
            </div>
          </div>
          {campaign.blog_title && (
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{campaign.blog_title}</p>
                <p className="text-xs text-gray-500 mt-1">Blog Article</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Current Status</span>
            {getStatusBadge()}
          </div>
          {campaign.scheduled_at && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Scheduled</span>
              <span className="font-medium text-gray-900">{formatDate(campaign.scheduled_at)}</span>
            </div>
          )}
          {campaign.sent_at && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Started</span>
              <span className="font-medium text-gray-900">{formatDate(campaign.sent_at)}</span>
            </div>
          )}
          {campaign.alert_is_late_started && !campaign.sent_at && (
            <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4" />
              <span>Had {Math.round(campaign.minutes_late)} minuten geleden moeten starten</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Metrics</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-2xl font-bold">{campaign.sent_count}</span>
              </div>
              <p className="text-xs text-gray-600">Sent ({campaign.success_rate_percent}%)</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-2xl font-bold">{campaign.failed_count}</span>
              </div>
              <p className="text-xs text-gray-600">Failed ({campaign.failure_rate_percent}%)</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                <Mail className="w-4 h-4" />
                <span className="text-2xl font-bold">{campaign.total_contacts}</span>
              </div>
              <p className="text-xs text-gray-600">Total</p>
            </div>
          </div>
          {campaign.alert_has_high_failure_rate && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              <AlertTriangle className="w-4 h-4" />
              <span>High failure rate detected!</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Quick Actions</h3>
        <div className="space-y-2">
          {campaign.status === 'scheduled' && (
            <button
              onClick={() => setShowSendNowModal(true)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg text-left transition-colors"
            >
              <Send className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-green-900">Verstuur Nu</p>
                <p className="text-sm text-green-700">Campaign direct versturen</p>
              </div>
            </button>
          )}
          {campaign.failed_count > 0 && (
            <button
              onClick={onRetry}
              className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-left transition-colors"
            >
              <RotateCcw className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Retry Failed Emails</p>
                <p className="text-sm text-blue-700">{campaign.failed_count} emails failed</p>
              </div>
            </button>
          )}
          <button
            onClick={onOpenDebugger}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-left transition-colors"
          >
            <Bug className="w-5 h-5 text-gray-600" />
            <div className="flex-1">
              <p className="font-medium text-gray-900">Debug in Email Debugger</p>
              <p className="text-sm text-gray-600">View detailed logs</p>
            </div>
          </button>
          {campaign.status === 'scheduled' && (
            <button
              onClick={onCancel}
              className="w-full flex items-center gap-3 px-4 py-3 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg text-left transition-colors"
            >
              <Ban className="w-5 h-5 text-orange-600" />
              <div className="flex-1">
                <p className="font-medium text-orange-900">Cancel Campaign</p>
                <p className="text-sm text-orange-700">Stop this scheduled campaign</p>
              </div>
            </button>
          )}
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-left transition-colors"
          >
            <Trash2 className="w-5 h-5 text-red-600" />
            <div className="flex-1">
              <p className="font-medium text-red-900">Verwijder Campaign</p>
              <p className="text-sm text-red-700">Permanent verwijderen uit database</p>
            </div>
          </button>
        </div>
      </div>

      {batches.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Batches ({batches.length})
          </h3>
          <div className="space-y-2">
            {batches.slice(0, 5).map((batch) => (
              <div key={batch.batch_api_call_id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    Batch {batch.batch_number}
                  </span>
                  <span className="text-xs text-gray-500">
                    {batch.duration_ms}ms
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-green-600">
                    ✓ {batch.emails_succeeded}/{batch.batch_size}
                  </span>
                  {batch.emails_failed > 0 && (
                    <span className="text-red-600">✗ {batch.emails_failed}</span>
                  )}
                  <span className="text-gray-500">{batch.success_rate_percent}%</span>
                </div>
              </div>
            ))}
            {batches.length > 5 && (
              <p className="text-xs text-center text-gray-500 py-2">
                +{batches.length - 5} more batches
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderDebugTab = () => (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Campaign ID</h3>
        <div className="bg-gray-50 rounded p-3">
          <code className="text-xs text-gray-700">{campaign.id}</code>
        </div>
      </div>

      {campaign.alert_is_failed || campaign.alert_is_late_started ||
       campaign.alert_has_high_failure_rate || campaign.alert_is_stuck_sending ? (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Issues Detected</h3>
          <div className="space-y-2">
            {campaign.alert_is_failed && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Campaign Failed</p>
                  <p className="text-xs text-red-700 mt-1">Campaign status is "failed"</p>
                </div>
              </div>
            )}
            {campaign.alert_is_late_started && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Clock className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Te Laat / Overdue</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Campaign had {Math.round(campaign.minutes_late)} minuten geleden moeten starten maar is nog niet begonnen
                  </p>
                </div>
              </div>
            )}
            {campaign.alert_has_high_failure_rate && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">High Failure Rate</p>
                  <p className="text-xs text-red-700 mt-1">
                    {campaign.failure_rate_percent}% of emails failed (threshold: 5%)
                  </p>
                </div>
              </div>
            )}
            {campaign.alert_is_stuck_sending && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Stuck Sending</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Sending for {Math.round(campaign.minutes_sending)} minutes
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-4 h-4" />
            <p className="text-sm font-medium">No issues detected</p>
          </div>
        </div>
      )}

      {errorLogs.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Error Logs ({errorLogs.length})
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {errorLogs.map((log) => (
              <div key={log.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm font-medium text-red-900">{log.recipient_email}</p>
                  <p className="text-xs text-red-700">{formatDate(log.sent_at)}</p>
                </div>
                <p className="text-xs text-red-800">{log.error_message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => {
            navigator.clipboard.writeText(JSON.stringify({
              campaign_id: campaign.id,
              status: campaign.status,
              sent_count: campaign.sent_count,
              failed_count: campaign.failed_count,
              batches: batches.length,
              errors: errorLogs.length
            }, null, 2));
          }}
          className="btn-secondary flex-1"
        >
          Copy Debug Info
        </button>
        <button
          onClick={onOpenDebugger}
          className="btn-primary flex-1"
        >
          Open Email Debugger
        </button>
      </div>
    </div>
  );

  const renderLogsTab = () => (
    <div className="p-6">
      <h3 className="text-sm font-medium text-gray-500 mb-4">Activity Timeline</h3>
      <div className="space-y-3">
        {activityLogs.map((log, index) => {
          const icons = {
            error: <XCircle className="w-4 h-4 text-red-600" />,
            success: <CheckCircle className="w-4 h-4 text-green-600" />,
            warning: <AlertTriangle className="w-4 h-4 text-yellow-600" />,
            info: <Clock className="w-4 h-4 text-blue-600" />
          };

          return (
            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 mt-0.5">{icons[log.icon]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900">{log.event}</p>
                  <p className="text-xs text-gray-500 flex-shrink-0">{formatDate(log.timestamp)}</p>
                </div>
                <p className="text-xs text-gray-600 mt-1">{log.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <button
          onClick={() => {
            const logText = activityLogs
              .map(log => `${formatDate(log.timestamp)} - ${log.event}: ${log.description}`)
              .join('\n');
            const blob = new Blob([logText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `campaign-${campaign.id}-logs.txt`;
            a.click();
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download Full Log
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="border-b border-gray-200">
          <div className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('debug')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'debug'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Debug Info
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'logs'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Activity Logs
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'debug' && renderDebugTab()}
          {activeTab === 'logs' && renderLogsTab()}
        </div>
      </div>

      {showSendNowModal && campaign && (
        <SendCampaignNowModal
          campaignId={campaign.id}
          campaignSubject={campaign.subject}
          subscriberCount={campaign.total_contacts}
          scheduledDate={campaign.scheduled_at}
          onClose={() => setShowSendNowModal(false)}
          onSuccess={() => {
            setShowSendNowModal(false);
            loadCampaignDetails();
          }}
        />
      )}
    </>
  );
}
